/**
 * Permission Guard Component
 * 
 * Conditionally renders children based on user permissions
 * 
 * Usage:
 * <PermissionGuard permission="CREATE_COMPETITION">
 *   <button>Create Competition</button>
 * </PermissionGuard>
 * 
 * <PermissionGuard 
 *   permissions={["EDIT_COMPETITION", "DELETE_COMPETITION"]} 
 *   requireAll={true}
 * >
 *   <button>Edit</button>
 * </PermissionGuard>
 * 
 * <PermissionGuard 
 *   role="admin"
 *   fallback={<div>Access Denied</div>}
 * >
 *   <AdminPanel />
 * </PermissionGuard>
 */

import React from 'react';
import { useAuth } from '../context/AuthContext';

const PermissionGuard = ({ 
  permission,        // Single permission name
  permissions,       // Array of permission names
  role,             // Role name to check
  scope,            // Optional scope for permission/role
  requireAll,       // If true, requires all permissions (default: false)
  fallback,         // Optional fallback component when not authorized
  children 
}) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions, hasRole } = useAuth();

  // Check role if specified
  if (role) {
    const hasRequiredRole = hasRole(role, scope);
    if (!hasRequiredRole) {
      return fallback || null;
    }
    return <>{children}</>;
  }

  // Check single permission
  if (permission) {
    const hasRequiredPermission = hasPermission(permission, scope);
    if (!hasRequiredPermission) {
      return fallback || null;
    }
    return <>{children}</>;
  }

  // Check multiple permissions
  if (permissions && Array.isArray(permissions)) {
    const hasRequiredPermissions = requireAll 
      ? hasAllPermissions(permissions)
      : hasAnyPermission(permissions);
    
    if (!hasRequiredPermissions) {
      return fallback || null;
    }
    return <>{children}</>;
  }

  // No permission/role specified, render children by default
  return <>{children}</>;
};

export default PermissionGuard;
